import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { createMongoAbility } from '@casl/ability';
import { useSelector } from 'react-redux';
import { getCurrentUserPermissions, getUserServices } from '../apis/permissions-api-new';
import cookieHelper from '../../Restore/library/helpers/cookieHelper';

/**
 * Map localStorage `userServiceAccess.services[0]` → numeric DB `service_id` for CASL.
 * Tokens are lowercase `service_key` from backend (often full names like `jiva dmp program`),
 * not always the legacy literals restore/sleep/dme.
 */
async function resolveNumericServiceIdForAbility(currentToken, permissions) {
  const permKeys = Object.keys(permissions || {});
  const raw =
    currentToken != null && currentToken !== ''
      ? currentToken
      : 'restore';

  const asNum = Number(raw);
  if (Number.isFinite(asNum) && permKeys.includes(String(asNum))) {
    return asNum;
  }

  const lc = String(raw).toLowerCase().trim();
  const legacyMap = {
    restore: 1,
    sleep: 2,
    dme: 3,
    '1': 1,
    '2': 2,
    '3': 3,
  };
  const mapped = legacyMap[lc];
  if (mapped != null && permKeys.includes(String(mapped))) {
    return mapped;
  }

  try {
    const services = await getUserServices();
    const list = Array.isArray(services) ? services : [];
    const exact = list.find((s) => {
      const key = String(s.service_key ?? '').toLowerCase();
      const name = String(s.name ?? '').toLowerCase();
      return key === lc || name === lc;
    });
    const hit =
      exact ||
      list.find((s) => {
        const key = String(s.service_key ?? '').toLowerCase();
        const name = String(s.name ?? '').toLowerCase();
        return (
          (lc && key.includes(lc)) ||
          (lc && lc.includes(key)) ||
          (lc && name.includes(lc)) ||
          (lc && lc.includes(name))
        );
      });

    if (hit?.service_id != null) {
      const sid = Number(hit.service_id);
      return Number.isFinite(sid) ? sid : legacyMap.restore;
    }
  } catch (e) {
    console.error('AbilityContext: getUserServices failed while resolving service_id', e);
  }

  return legacyMap.restore;
}

/**
 * Pure CASL implementation following official documentation
 * https://casl.js.org/v6/en/package/casl-react
 * 
 * NESTED PERMISSION INHERITANCE SYSTEM:
 * 
 * This system handles cases where the backend only provides permissions for parent modules,
 * but the UI has nested child modules that should inherit those permissions.
 * 
 * Example: Backend provides permissions for "ManageUsers" (read: true, write: true, edit: true, delete: true)
 *          UI has nested modules: "Patients" and "CareTeam" (Staff)
 *          Solution: Automatically inherit ManageUsers permissions to Patients and CareTeam
 * 
 * Parent-Child Relationships:
 * - ManageUsers → Patients, CareTeam
 * - Appointments → Availability, calendarView, tableView, todayAppointment  
 * - DigitalLibrary → manageContent, manageSchedule
 * - Reports → rPMdashboard, CPTReportDownload, WeightReport
 * - CareTeam → staffList, coachesList, providerList
 * 
 * How it works:
 * 1. When processing backend permissions, if a parent module has permissions,
 *    automatically create the same permissions for all its child modules
 * 2. In the sidebar filtering, check both direct permissions and inherited permissions
 * 3. In individual component permission checks (Can components), also check inherited permissions
 * 
 * This ensures that if a user has "ManageUsers" permissions, they can access both
 * "Patients" and "CareTeam" sections without needing separate backend permissions.
 */

// Create ability context
export const AbilityContext = createContext();

// Create Can component using the new CASL v6 pattern
// Supports 'this' as a single string, an array of strings, or a comma-separated string
export const Can = ({ I, this: subject, children, passThrough = false }) => {
  const context = useContext(AbilityContext);
  
  const ability = context?.ability;
  
  let canPerform = false;
  if (ability && subject) {
    if (Array.isArray(subject)) {
      canPerform = subject.some(s => ability.can(I, s));
    } else if (typeof subject === 'string' && subject.includes(',')) {
      canPerform = subject.split(',').map(s => s.trim()).some(s => ability.can(I, s));
    } else {
      canPerform = ability.can(I, subject);
    }
  }
  
  if (typeof children === 'function') {
    return children(canPerform, ability);
  }

  if (passThrough) {
    return children;
  }
  
  return canPerform ? children : null;
};

/**
 * Transform backend permissions to CASL rules
 */
function defineRulesFor(permissions) {
  let rules = [];

  if (!permissions || typeof permissions !== 'object') {
    return rules;
  }

     // Remove module hierarchy - since we have hierarchical module names,
   // each module should have its own specific permissions from the backend
   // No inheritance needed

  // Handle flat structure (direct module permissions) vs nested structure (service-based)
  let modulesToProcess = [];
  
  if (permissions.hasOwnProperty('1') || permissions.hasOwnProperty('2')) {
    // Nested structure: { "1": { "AlertConfig": {...} } }
    Object.entries(permissions).forEach(([serviceId, servicePerms]) => {
      if (!servicePerms || typeof servicePerms !== 'object') return;
      const numericServiceId = parseInt(serviceId);
      
      Object.entries(servicePerms).forEach(([module, modulePerms]) => {
        if (!modulePerms || typeof modulePerms !== 'object') return;
        modulesToProcess.push({ module, modulePerms, serviceId: numericServiceId });
      });
    });
  } else {
    // Flat structure: { "AlertConfig": {...} }
    const defaultServiceId = 1; // Assume Restore service for flat structure
    Object.entries(permissions).forEach(([module, modulePerms]) => {
      if (!modulePerms || typeof modulePerms !== 'object') return;
      modulesToProcess.push({ module, modulePerms, serviceId: defaultServiceId });
    });
  }

  // Process each module and create rules
  
  modulesToProcess.forEach(({ module, modulePerms, serviceId }) => {
    const { read, write, edit, delete: deletePermission, role_name } = modulePerms;
    
         // Create rules for the current module
     if (read) {
       const rule = { action: 'read', subject: module };
       rules.push(rule);
     }
    
    if (write) {
      const rule = { action: 'create', subject: module };
      rules.push(rule);
    }
    if (edit) {
      const rule = { action: 'update', subject: module };
      rules.push(rule);
    }
    if (deletePermission) {
      const rule = { action: 'delete', subject: module };
      rules.push(rule);
    }

    // Custom actions (e.g. cancel_subscription) - any key other than read/write/edit/delete/role_name
    const standardKeys = ['read', 'write', 'edit', 'delete', 'role_name'];
    Object.entries(modulePerms).forEach(([action, enabled]) => {
      if (standardKeys.includes(action) || !enabled) return;
      rules.push({ action, subject: module });
    });

    // Remove individual manage rules - let specific permissions take precedence
    // This ensures that even admin users are restricted by specific module permissions
    // if (role_name && ['admin', 'sleep_admin'].includes(role_name.toLowerCase())) {
    //   const rule = { action: 'manage', subject: module };
    //   rules.push(rule);
    // }

         // Remove inheritance logic - since we have hierarchical module names, 
     // each module should have its own specific permissions from the backend
     // No inheritance needed
  });

     // Remove parent-child restriction logic - since we have hierarchical module names,
   // each module should have its own specific permissions from the backend
   // No parent-child restrictions needed

  // Remove global manage rule - let specific permissions take precedence
  // This ensures that even admin users are restricted by specific module permissions
  // const hasAdminRole = modulesToProcess.some(({ modulePerms }) => 
  //   modulePerms.role_name && ['admin', 'sleep_admin'].includes(modulePerms.role_name.toLowerCase())
  // );
  
  // if (hasAdminRole) {
  //   const globalRule = { action: 'manage', subject: 'all' };
  //   rules.push(globalRule);
  // }

  return rules;
}

/**
 * AbilityProvider - provides CASL ability to all children
 */
export const AbilityProvider = ({ children }) => {
  const [ability, setAbility] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const idToken = useSelector((state) => state?.Auth?.idToken);
  const loadRequestIdRef = useRef(0);

  const loadAbility = async () => {
    const requestId = ++loadRequestIdRef.current;
    const initialToken = cookieHelper.getToken() || localStorage.getItem('id_token') || '';
    try {
      setLoading(true);
      setError(null);

      // Get user ID
      const userDetails = cookieHelper.getUserDetails() || '{}';
      if (!userDetails.staffId) {
        if (requestId === loadRequestIdRef.current) {
          // Not logged in yet; provide a no-op ability and skip loading error
          setAbility(createMongoAbility([]));
          setLoading(false);
        }
        return;
      }

      // Get current service context (Home stores lowercase service_key strings, not only 1|2|3)
      const userServiceAccess = JSON.parse(localStorage.getItem('userServiceAccess') || '{}');
      const currentServiceToken =
        Array.isArray(userServiceAccess.services) && userServiceAccess.services.length > 0
          ? userServiceAccess.services[0]
          : 'restore';

      // Fetch permissions from backend (keyed by numeric service_id)
      const permissions = await getCurrentUserPermissions();

      const numericServiceId = await resolveNumericServiceIdForAbility(
        currentServiceToken,
        permissions
      );

      // Filter permissions for current service only
      const currentServicePermissions =
        permissions[numericServiceId] || permissions[String(numericServiceId)] || {};

      // Transform to CASL rules (only for current service)
      const rules = defineRulesFor({ [numericServiceId]: currentServicePermissions });

      const currentToken = cookieHelper.getToken() || localStorage.getItem('id_token') || '';

      // Update ability only if this load is still current and token has not changed
      if (requestId === loadRequestIdRef.current && currentToken === initialToken) {
        const newAbility = createMongoAbility(rules);
        setAbility(newAbility);
      }

    } catch (err) {
      console.error('Failed to load ability:', err);
      if (requestId === loadRequestIdRef.current) {
        // Do not block app: fallback to empty ability
        setAbility(createMongoAbility([]));
        setError(err.message);
      }
    } finally {
      if (requestId === loadRequestIdRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    loadAbility();
  }, []);

  // Refresh ability whenever auth state changes so permissions are available
  // immediately after login without requiring a full page reload.
  useEffect(() => {
    if (idToken) {
      // Only reload if service context is already established (e.g., page refresh with
      // a valid session). On a fresh login, userServiceAccess is cleared by the logout
      // saga so there is nothing to load yet — the storage listener handles it once
      // SignIn.js or Home.jsx writes the correct service after quickPermissionCheck.
      const existing = JSON.parse(localStorage.getItem('userServiceAccess') || '{}');
      if (existing.services?.length > 0) {
        loadAbility();
      }
      return;
    }

    setAbility(createMongoAbility([]));
    setLoading(false);
  }, [idToken]);

  // Listen for service context and auth changes
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'userServiceAccess') {
        loadAbility();
      }
    };

    // Reload permissions when login-as state changes or when permissions are explicitly saved/auth state changes
    const handleRefresh = () => loadAbility();

    const handleLoggedOut = () => {
      loadRequestIdRef.current++;
      setAbility(createMongoAbility([]));
      setLoading(false);
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('loginAsStatusChanged', handleRefresh);
    window.addEventListener('permissionsUpdated', handleRefresh);
    window.addEventListener('authStateChanged', handleRefresh);
    window.addEventListener('userLoggedOut', handleLoggedOut);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('loginAsStatusChanged', handleRefresh);
      window.removeEventListener('permissionsUpdated', handleRefresh);
      window.removeEventListener('authStateChanged', handleRefresh);
      window.removeEventListener('userLoggedOut', handleLoggedOut);
    };
  }, []);

  // Create context value with ability and reload function
  // Always provide ability (even if null) to prevent blocking
  const contextValue = {
    ability: ability || createMongoAbility([]), // Never null - always provide empty ability as fallback
    reloadAbility: loadAbility,
    loading,
    error
  };
  
  // Don't block rendering - always render children even if loading
  return (
    <AbilityContext.Provider value={contextValue}>
      {children}
    </AbilityContext.Provider>
  );
};

/**
 * Hook to access ability context
 */
export const useAbility = () => {
  const context = useContext(AbilityContext);
  if (!context) {
    throw new Error('useAbility must be used within AbilityProvider');
  }
  return context;
};

export default AbilityContext;